import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { auth, db } from './firebase';

export type ShareContentType = 'post' | 'short' | 'long' | 'live';

export const canonicalShareUrl = (contentType: ShareContentType, contentId: string): string =>
  `https://clue200.app/c/${contentType}/${encodeURIComponent(contentId)}`;

export const shareContent = async (
  contentType: ShareContentType,
  contentId: string,
  title: string
): Promise<string> => {
  const url = canonicalShareUrl(contentType, contentId);
  if (typeof navigator.share === 'function') {
    await navigator.share({ title, text: title, url });
  } else {
    await navigator.clipboard.writeText(url);
  }
  return url;
};

export interface ReshareRecord {
  id: string;
  originalPostId: string;
  originalOwnerUid: string;
  resharedByUid: string;
  resharedAt?: { toDate?: () => Date };
}

export const resharePost = async (
  originalPostId: string,
  originalOwnerUid: string
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication is required to reshare a post.');
  const existing = await new Promise<string | null>(resolve => {
    let unsubscribe = () => {};
    unsubscribe = onSnapshot(
      query(
        collection(db, 'reshares'),
        where('originalPostId', '==', originalPostId),
        where('resharedByUid', '==', user.uid),
        limit(1)
      ),
      snapshot => {
        unsubscribe();
        resolve(snapshot.docs[0]?.id ?? null);
      },
      () => {
        unsubscribe();
        resolve(null);
      }
    );
  });
  if (existing) return existing;
  const created = await addDoc(collection(db, 'reshares'), {
    originalPostId,
    originalOwnerUid,
    resharedByUid: user.uid,
    resharedAt: serverTimestamp()
  });
  return created.id;
};

export const unresharePost = async (reshareId: string): Promise<void> => {
  if (!auth.currentUser) throw new Error('Authentication is required to un-reshare a post.');
  await deleteDoc(doc(db, 'reshares', reshareId));
};
